import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UiConfig } from '@models/config';
import { FilterTemplate } from '@models/filter-types';
import { SavedReport, SavedReportData, SavedReportSomething } from '@models/lookup';
import { AuthService } from '@services/auth/auth.service';
import { LookupV2Service } from '@services/lookup-v2/lookup-v2.service';
import { NotificationService } from '@services/notification/notification.service';
import { ConfirmDialogComponent, CONFIRM_DIALOG } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { MessageDialogComponent } from '@shared/components/message-dialog/message-dialog.component';
import { TextDialogComponent } from '@shared/components/text-dialog/text-dialog.component';
import { isConfiguredEmailReport, isEndDatePassed, segmentTrack } from '@shared/helpers/functions/helpers';
import { EmailReportsPopUpComponent } from '@shared/components/report-components/email-reports-pop-up/email-reports-pop-up.component';
@Component({
    selector: 'app-load-report-popup',
    templateUrl: './load-report-popup.component.html',
    styleUrls: ['./load-report-popup.component.scss'],
    standalone: false,
})
export class LoadReportPopupComponent implements OnInit {
    public savedReports: SavedReport[] = [];
    public favoriteReports: SavedReport[] = [];
    public expiredReports: SavedReport[] = [];
    public globalReports: SavedReport[] = [];
    public myReports: SavedReport[] = [];
    public myGlobalReports: SavedReport[] = [];

    public url: string;
    public filter: SavedReportSomething | FilterTemplate;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public parentFilter: SavedReportSomething | FilterTemplate | any;
    report!: SavedReport;
    isLoading = true;
    isDeleting = false;
    userConfig!: UiConfig;
    isEditableRow = false;

    constructor(
        public dialogRef: MatDialogRef<LoadReportPopupComponent>,
        public matDialog: MatDialog,
        private authSvc: AuthService,
        private lookupV2Service: LookupV2Service,
        private snackbarService: NotificationService,
        @Inject(MAT_DIALOG_DATA) private data: SavedReportData,
    ) {
        this.url = data.url;
        this.parentFilter = data.filter;
        this.savedReports = data.reports;
        this.categorizeReports();

        const editableTabs = ['dynamic_rate_card', 'dashboard'];
        this.isEditableRow = !!data.tabId && editableTabs.some((tab) => data.tabId.includes(tab));
    }

    ngOnInit(): void {
        this.userConfig = this.authSvc.getConfig();
    }

    sortReportsByName(reports: SavedReport[]) {
        reports.sort((a, b) => a.reportName.toLowerCase().localeCompare(b.reportName.toLowerCase()));
    }

    openEditReportDialog(report: SavedReport) {
        const loadObj = {
            savedReports: this.savedReports,
            categorizeReports: () => this.categorizeReports(),
        };
        this.parentFilter.editReport(report, loadObj);
    }

    toggleIsFavorited(report: SavedReport) {
        const screenName = this.url;
        const isFavoritedValue = !report.isFavorited;
        this.lookupV2Service.toggleSaveReportIsFavorited(screenName, report.reportId, isFavoritedValue).subscribe(() => {
            const reportIndex = this.savedReports.findIndex((r) => r.reportId === report.reportId);
            this.savedReports[reportIndex].isFavorited = isFavoritedValue;
            this.categorizeReports();
        });
    }

    openEmailReportDialog(report: SavedReport) {
        this.matDialog.open(EmailReportsPopUpComponent, {
            width: '24em',
            data: {
                report,
            },
        });
    }

    isConfiguredEmailReport(report: SavedReport) {
        return isConfiguredEmailReport(report);
    }

    isEndDatePassed(endDate: unknown) {
        return isEndDatePassed(endDate);
    }

    copyReportLink(report: SavedReport) {
        const shareableURL = new URL(window.location.origin + window.location.pathname.split('/r/')[0].split('/t/')[0]);
        if (this.data.tabId) {
            shareableURL.pathname += `/t/${this.data.tabId}`;
        }
        this.lookupV2Service.createSharedReport(report.reportId).subscribe((shareId) => {
            shareableURL.pathname += `/r/${shareId}`;
            navigator.clipboard
                .writeText(shareableURL.toString())
                .then(() => {
                    const copyDialogRef = this.matDialog.open(MessageDialogComponent, {
                        data: {
                            message: 'Copied Report URL',
                        },
                    });
                    copyDialogRef.afterOpened().subscribe((_) => {
                        setTimeout(() => {
                            copyDialogRef.close();
                        }, 2000);
                    });
                })
                .catch((err) => console.error(err));
        });
    }

    openDeleteReportDialog(report: SavedReport) {
        if (report.isGlobal && !this.userConfig.roleCapability.rate_optics.general.create_global_report) {
            this.matDialog.open(TextDialogComponent, {
                data: {
                    text: 'You do not have the permission to delete global reports',
                    trueButtonText: 'Ok',
                    falseButtonText: undefined,
                    noClickClose: true,
                },
            });
            return;
        }
        const dialog = this.matDialog.open(ConfirmDialogComponent, {
            data: {
                text: `Delete ${report.reportName}?`,
                autoConfirm: false,
                noClickClose: true,
                confirmText: 'Yes',
                denyText: 'No',
                denyClose: true,
            },
        });

        dialog.afterClosed().subscribe((result) => {
            if (result === CONFIRM_DIALOG) {
                const screenName = this.url;
                this.lookupV2Service.deleteSavedReport(screenName, report.reportId).subscribe(
                    () => {
                        const reportIndex = this.savedReports.findIndex((r) => r.reportId === report.reportId);
                        this.savedReports.splice(reportIndex, 1);
                        this.categorizeReports();
                        this.snackbarService.openSnackBar('Deleted Report', 'success');
                    },
                    (error) => {
                        console.error(error);
                        this.snackbarService.openSnackBar('Could not delete report', 'error');
                    },
                );
            }
        });
    }

    loadFilters(report: SavedReport) {
        if (report.filter) {
            this.filter = report.filter;
            this.filter.savedMetrics = report.savedMetrics.savedMetrics;
            this.filter.savedMetricsOrder = report.savedMetrics.savedMetricsOrder;
            this.filter.savedSegments = report.savedSegments.savedSegments;
            this.filter.savedSegmentsOrder = report.savedSegments.savedSegmentsOrder;
            this.filter.savedDemos = report.savedDemos?.savedDemos || [];
            this.filter.savedDemosOrder = report.savedDemos?.savedDemosOrder || [];
        }
        this.report = report;
    }

    saveReportAccess() {
        const reportsAccessData = this.savedReports.map((report) => ({
            reportId: report.reportId,
            userId: report.userId,
            isFavorited: report.isFavorited,
        }));
        const screenName = this.url;
        this.lookupV2Service.userSaveReportAccess(screenName, reportsAccessData).subscribe(() => {});
    }

    loadFiltersAndClose() {
        this.report.data = this.data;
        this.dialogRef.close({ filter: this.filter, selectedReport: this.report });
        segmentTrack('User Selected Report', {
            module: this.report?.data?.url,
            reportId: this.report?.reportId,
            reportName: this.report?.reportName,
            isFavorited: this.report?.isFavorited,
            isGlobal: this.report?.isGlobal,
            useRollingDates: this.report?.filter?.useRollingDates,
        });
    }
    closeDialog() {
        this.dialogRef.close();
    }

    categorizeReports(): void {
        this.favoriteReports = [];
        this.myReports = [];
        this.globalReports = [];
        this.myGlobalReports = [];

        this.savedReports.forEach((report) => {
            const isFavorited = report.isFavorited;
            const isGlobal = report.isGlobal;
            const isExpired = report.isExpired;

            if (isExpired) {
                this.expiredReports.push(report);
            } else if (isFavorited) {
                this.favoriteReports.push(report);
            } else if (!isFavorited && !isGlobal) {
                this.myReports.push(report);
            } else if (!isFavorited && isGlobal) {
                (report.isReportOwner ? this.myGlobalReports : this.globalReports).push(report);
            }
        });
        this.sortReportsByName(this.expiredReports);
        this.sortReportsByName(this.favoriteReports);
        this.sortReportsByName(this.myReports);
        this.sortReportsByName(this.globalReports);
        this.sortReportsByName(this.myGlobalReports);
    }
}
